import type { SetBLdMotivationKey } from '@shared/constants/setBLdMotivation';

/** localStorage draft for Set B /ldproductnow. */
export const LD_PRODUCT_NOW_STORAGE_KEY = 'ldsurvey:ldProductNow';

export type LdMotivationKey = SetBLdMotivationKey;

export type LdProductNowAnswers = {
  variant: string;
  /** Single motivation selection. */
  motivation: LdMotivationKey | '';
  motivationOtherSpecify: string;
  smokedOtherProduct: '' | 'yes' | 'no';
};

export const emptyLdProductNowAnswers = (): LdProductNowAnswers => ({
  variant: '',
  motivation: '',
  motivationOtherSpecify: '',
  smokedOtherProduct: '',
});

const MOTIVATION_KEYS: LdMotivationKey[] = [
  'productQuality',
  'price',
  'peerRecommendation',
  'strongTaste',
  'smoothDelivery',
  'mentholFlavorExperience',
  'other',
];

function parseLegacyMotivations(value: unknown): Pick<LdProductNowAnswers, 'motivation' | 'motivationOtherSpecify'> {
  if (typeof value !== 'object' || value === null) {
    return { motivation: '', motivationOtherSpecify: '' };
  }
  const o = value as Record<string, unknown>;
  const otherSpecify = typeof o.otherSpecify === 'string' ? o.otherSpecify : '';
  if (otherSpecify.trim()) {
    return { motivation: 'other', motivationOtherSpecify: otherSpecify };
  }
  for (const key of MOTIVATION_KEYS) {
    if (key !== 'other' && o[key] === true) {
      return { motivation: key, motivationOtherSpecify: '' };
    }
  }
  return { motivation: '', motivationOtherSpecify: '' };
}

export function readLdProductNowAnswers(): LdProductNowAnswers {
  try {
    const raw = localStorage.getItem(LD_PRODUCT_NOW_STORAGE_KEY);
    if (!raw) return emptyLdProductNowAnswers();
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return emptyLdProductNowAnswers();
    const o = parsed as Record<string, unknown>;
    const smoked =
      o.smokedOtherProduct === 'yes' || o.smokedOtherProduct === 'no' ? o.smokedOtherProduct : '';

    let motivation: LdProductNowAnswers['motivation'] = '';
    let motivationOtherSpecify = '';

    if (typeof o.motivation === 'string' && MOTIVATION_KEYS.includes(o.motivation as LdMotivationKey)) {
      motivation = o.motivation as LdMotivationKey;
      motivationOtherSpecify =
        typeof o.motivationOtherSpecify === 'string' ? o.motivationOtherSpecify : '';
    } else if (o.motivations != null) {
      const legacy = parseLegacyMotivations(o.motivations);
      motivation = legacy.motivation;
      motivationOtherSpecify = legacy.motivationOtherSpecify;
    }

    return {
      variant: typeof o.variant === 'string' ? o.variant : '',
      motivation,
      motivationOtherSpecify,
      smokedOtherProduct: smoked,
    };
  } catch {
    return emptyLdProductNowAnswers();
  }
}

export function writeLdProductNowAnswers(data: LdProductNowAnswers): void {
  try {
    localStorage.setItem(LD_PRODUCT_NOW_STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

export function clearLdProductNowAnswers(): void {
  try {
    localStorage.removeItem(LD_PRODUCT_NOW_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function hasLdMotivationSelected(answers: LdProductNowAnswers): boolean {
  if (!answers.motivation) return false;
  if (answers.motivation === 'other') return answers.motivationOtherSpecify.trim().length > 0;
  return true;
}

export function isLdProductNowComplete(): boolean {
  const data = readLdProductNowAnswers();
  if (!data.variant.trim()) return false;
  if (!hasLdMotivationSelected(data)) return false;
  return data.smokedOtherProduct === 'yes' || data.smokedOtherProduct === 'no';
}

export const LD_MOTIVATION_KEYS = MOTIVATION_KEYS;
